import React, { useId, useState } from "react";
import {
  Check,
  Clear,
  Delete,
  Edit,
  MoreVert,
  Star,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Team as TeamType, User } from "../../types";
import { UserList } from "../User/List";
import { UserAutocomplete } from "../User/Autocomplete";
import { getLabelWithRemainingChars } from "../../Helpers";

function EmptyState() {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h5" sx={{ textAlign: "center", mb: 2 }}>
        It&apos;s looking pretty empty in here...
      </Typography>
      <Typography>
        Assign members to this team by selecting them from this dropdown.
      </Typography>
    </Box>
  );
}

export interface Props {
  team: TeamType;
  users: User[];
  onChange?: (team: TeamType) => void;
  onDelete?: (team: TeamType) => void;
  onAssign: (userIds: string[], team: TeamType) => void;
  onSetLeader?: (userId: string, team: TeamType) => void;
  onEditingChange?: (teamId: string | null) => void;
}

export const TeamEdit = ({
  team,
  onAssign,
  onChange,
  onDelete,
  onSetLeader,
  users,
  onEditingChange,
}: Props) => {
  const theme = useTheme();
  const teamUsers = users.filter((user) => team.userIds.indexOf(user.id) >= 0);
  const [menuAnchor, setMenuAnchor] = React.useState<
    [HTMLElement, User] | null
  >(null);
  const menuOpen = Boolean(menuAnchor);

  //
  // State
  //

  const [isEditing, setIsEditing] = useState(false);
  const [teamName, setTeamName] = useState(team.name);

  //
  // Event handlers
  //

  function handleDeleteUser(user: User) {
    const clone = {
      ...team,
      userIds: team.userIds.filter((target) => target !== user.id),
    };
    onChange && onChange(clone);
  }

  function handleAssignLeader(user: User) {
    onSetLeader && onSetLeader(user.id, team);
  }

  function handleMenuClick(user: User, e: React.MouseEvent<HTMLElement>) {
    setMenuAnchor([e.currentTarget, user]);
  }

  function handleMenuClose() {
    setMenuAnchor(null);
  }

  function handleSave() {
    onEditingChange && onEditingChange(team.id);
    onChange && onChange({ ...team, name: teamName });
    setIsEditing(false);
  }

  const menuId = useId();

  return (
    <Card
      sx={{
        mb: 2,
        border: `3px solid ${
          team.userIds.length ? "transparent" : theme.palette.primary.main
        }`,
      }}
    >
      <CardHeader
        title={
          isEditing ? (
            <TextField
              variant="outlined"
              label={getLabelWithRemainingChars(
                "Team Name",
                team.name.length,
                255,
              )}
              size="small"
              value={teamName}
              fullWidth={true}
              onChange={(e) => setTeamName(e.target.value)}
              inputProps={{ maxLength: 255 }}
            />
          ) : (
            team.name
          )
        }
        action={
          <Box display="flex" gap={1} sx={{ mr: 1 }}>
            {isEditing && (
              <Button
                variant="contained"
                sx={{ mt: 0.5, ml: 1 }}
                onClick={handleSave}
              >
                Finish Editing <Check />
              </Button>
            )}
            {!isEditing && (
              <IconButton
                edge="end"
                aria-label="edit team name"
                onClick={() => {
                  setIsEditing(true);
                }}
              >
                <Edit />
              </IconButton>
            )}
            {onDelete && (
              <IconButton
                edge="end"
                aria-label="exclude from campaign"
                onClick={() => onDelete && onDelete(team)}
              >
                <Delete />
              </IconButton>
            )}
          </Box>
        }
      />
      <CardContent>
        {!teamUsers.length && <EmptyState />}
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <UserAutocomplete
            label="Assign Team Members"
            onChange={(user) => onAssign([user.id], team)}
            showAvatars={true}
            users={users.filter(
              (target) => team.userIds.indexOf(target.id) < 0,
            )}
          />
        </Box>
        <UserList
          users={teamUsers}
          leader={team.leaderId}
          UserActions={({ user }) =>
            onChange && (
              <IconButton
                onClick={(e) => handleMenuClick(user, e)}
                size="small"
                sx={{ ml: 2 }}
                aria-label={`team member actions for ${user.firstName} ${user.lastName}`}
                aria-controls={menuOpen ? "user-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? "true" : undefined}
              >
                <MoreVert />
              </IconButton>
            )
          }
        />
      </CardContent>
      <Menu
        anchorEl={menuAnchor && menuAnchor[0]}
        id={menuId}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => menuAnchor && handleAssignLeader(menuAnchor[1])}
        >
          <ListItemIcon>
            <Star fontSize="small" />
          </ListItemIcon>
          Assign as lead
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => menuAnchor && handleDeleteUser(menuAnchor[1])}>
          <ListItemIcon>
            <Clear fontSize="small" />
          </ListItemIcon>
          Remove from team
        </MenuItem>
      </Menu>
    </Card>
  );
};
